import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  Avatar,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Badge,
  Chip,
  CircularProgress,
  Snackbar
} from '@mui/material';
import {
  Person as PersonIcon,
  Security as SecurityIcon,
  Settings as SettingsIcon,
  Assessment as AssessmentIcon,
  Notifications as NotificationsIcon,
  Edit as EditIcon,
  PhotoCamera as PhotoCameraIcon,
  Save as SaveIcon,
  Lock as LockIcon,
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import api from '../../config/axiosConfig';
import { capitalizePersonName } from '../../utils/textCase';
import CaseTypoHint from '../common/CaseTypoHint';
import { FIELD_LIMITS } from '../../constants/fieldLimits';
import AvanceMiServicio from './AvanceMiServicio';
import { useLeaveGuard } from '../../hooks/useLeaveGuard';
import userService from '../../services/userService';
import ApiTokensSection from './ApiTokensSection';
import { isJpgOrPng, JPG_PNG_ACCEPT } from '../../utils/imageFile';

const GUEST_SURNAME_PLACEHOLDER = 'Invitado';
const NAME_FIELDS = [
  { key: 'firstName', label: 'Nombre' },
  { key: 'firstLastName', label: 'Apellido Paterno' },
  { key: 'secondLastName', label: 'Apellido Materno' },
];
const isNameMissing = (value) => !value || !value.trim() || value.trim().toLowerCase() === GUEST_SURNAME_PLACEHOLDER.toLowerCase();
const sanitizeNameInput = (value) => value.replace(/[^\p{L} '\-.]/gu, '');

const TabPanel = ({ children, value, index }) => (
  <div hidden={value !== index}>
    {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
  </div>
);

const Profile = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [editMode, setEditMode] = useState(false);
  // Nombre tal como está guardado: para un invitado, lo capturado queda bloqueado y solo se
  // llenan los campos vacíos.
  const [savedNames, setSavedNames] = useState({ firstName: '', firstLastName: '', secondLastName: '' });
  const [changePasswordDialog, setChangePasswordDialog] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [userProfile, setUserProfile] = useState({
  firstName: '',
  firstLastName: '',
    secondLastName: '',
    email: '',
    username: '',
  // ...existing code...
  // ...existing code...
    isActive: false,
    createdAt: null,
    theme: 'light',
    language: 'es',
    timezone: 'America/Mexico_City'
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoSuccess, setLogoSuccess] = useState(false);
  const logoInputRef = useRef(null);

  const [propertyImageFile, setPropertyImageFile] = useState(null);
  const [propertyImagePreview, setPropertyImagePreview] = useState(null);
  const [propertyImageUploading, setPropertyImageUploading] = useState(false);
  const [propertyImageSuccess, setPropertyImageSuccess] = useState(false);
  const propertyImageInputRef = useRef(null);

  const [companyData, setCompanyData] = useState({
    businessName: '', legalName: '', rfc: '', requiresInvoice: false,
    fiscalStreet: '', fiscalExteriorNumber: '', fiscalInteriorNumber: '',
    fiscalNeighborhood: '', fiscalMunicipality: '', fiscalState: '',
    fiscalPostalCode: '', fiscalReference: '', fiscalBetweenStreets: '', fiscalCountry: 'México',
  });
  const [constanciaFile, setConstanciaFile] = useState(null);
  const [constanciaUploading, setConstanciaUploading] = useState(false);
  const [constanciaSuccess, setConstanciaSuccess] = useState(false);
  const [savingCompany, setSavingCompany] = useState(false);
  const constanciaInputRef = useRef(null);

  const isInvitado = userProfile.role === 'INVITADO';

  const handleConstanciaSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.type !== 'application/pdf') { setError('Solo se permiten archivos PDF'); return; }
    if (file.size > 10 * 1024 * 1024) { setError('El archivo no puede superar 10 MB'); return; }
    setConstanciaFile(file);
    setError('');
  };

  const handleConstanciaUpload = async () => {
    if (!constanciaFile || !userProfile.company?.id) return false;
    setConstanciaUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', constanciaFile);
      await api.post(`/companies/${userProfile.company.id}/constancia-fiscal`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setConstanciaSuccess(true);
      setConstanciaFile(null);
      setUserProfile(prev => ({ ...prev, company: { ...prev.company, hasConstancia: true } }));
      setTimeout(() => setConstanciaSuccess(false), 4000);
      return true;
    } catch (err) { setError(err.response?.data?.error || 'Error al subir la constancia fiscal.'); return false; }
    finally { setConstanciaUploading(false); }
  };

  // Archivos elegidos pero sin subir: al guardar los datos de la empresa se pregunta si se suben,
  // para que el cliente no los pierda por olvidar el botón "Subir".
  const [pendingUploadConfirm, setPendingUploadConfirm] = useState(false);
  const pendingUploads = [
    { label: 'Constancia Fiscal', file: constanciaFile, upload: () => handleConstanciaUpload() },
    { label: 'Logo', file: logoFile, upload: () => handleLogoUpload() },
    { label: 'Fotografía del inmueble', file: propertyImageFile, upload: () => handlePropertyImageUpload() },
  ].filter(u => u.file);

  useLeaveGuard(pendingUploads.length > 0,
    `Tienes archivos seleccionados sin subir (${pendingUploads.map(u => u.label).join(', ')}). Si sales, se perderán. ¿Salir de todos modos?`);

  const handleSaveCompanyClick = () => {
    if (pendingUploads.length > 0) {
      setPendingUploadConfirm(true);
      return;
    }
    handleSaveCompany();
  };

  const handleSaveCompany = async ({ uploadPending = false } = {}) => {
    if (!userProfile.company?.id) return;
    setSavingCompany(true);
    try {
      await api.put(`/companies/${userProfile.company.id}/self-profile`, {
        ...companyData,
        companyType: userProfile.company.companyType || 'INDEPENDIENTE',
        isActive: userProfile.company.isActive !== undefined ? userProfile.company.isActive : true,
      });
      if (uploadPending) {
        const failed = [];
        for (const item of pendingUploads) {
          if (!(await item.upload())) failed.push(item.label);
        }
        if (failed.length > 0) {
          setError(`Los datos de la empresa se guardaron, pero no se pudo subir: ${failed.join(', ')}. Intenta subirlo de nuevo.`);
          return;
        }
        setSuccess('Información de la empresa actualizada y archivos subidos.');
      } else {
        setSuccess('Información de la empresa actualizada.');
      }
      setTimeout(() => setSuccess(''), 3000);
    } catch { setError('Error al guardar la información de la empresa.'); }
    finally { setSavingCompany(false); }
  };

  const loadCompanyImagePreview = async (companyId, endpoint, setPreview) => {
    try {
      const response = await api.get(`/companies/${companyId}/${endpoint}`, { responseType: 'blob' });
      setPreview(URL.createObjectURL(response.data));
    } catch (err) {
      console.warn(`No se pudo cargar la imagen existente (${endpoint}):`, err);
    }
  };

  const handleLogoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!isJpgOrPng(file)) {
      setError('Solo se permiten archivos .jpg o .png');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('El archivo no puede superar 5 MB');
      return;
    }
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
    setError('');
  };

  const handleLogoUpload = async () => {
    if (!logoFile || !userProfile.company?.id) return false;
    setLogoUploading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', logoFile);
      await api.post(`/companies/${userProfile.company.id}/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setLogoSuccess(true);
      setLogoFile(null);
      setUserProfile(prev => ({ ...prev, company: { ...prev.company, hasLogo: true } }));
      setTimeout(() => setLogoSuccess(false), 4000);
      return true;
    } catch (err) {
      setError(err.response?.data?.error || 'Error al subir el logo. Intenta de nuevo.');
      return false;
    } finally {
      setLogoUploading(false);
    }
  };

  const handlePropertyImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!isJpgOrPng(file)) {
      setError('Solo se permiten archivos .jpg o .png');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setError('El archivo no puede superar 8 MB');
      return;
    }
    setPropertyImageFile(file);
    setPropertyImagePreview(URL.createObjectURL(file));
    setError('');
  };

  const handlePropertyImageUpload = async () => {
    if (!propertyImageFile || !userProfile.company?.id) return false;
    setPropertyImageUploading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', propertyImageFile);
      await api.post(`/companies/${userProfile.company.id}/property-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPropertyImageSuccess(true);
      setPropertyImageFile(null);
      setUserProfile(prev => ({ ...prev, company: { ...prev.company, hasPropertyImage: true } }));
      setTimeout(() => setPropertyImageSuccess(false), 4000);
      return true;
    } catch (err) {
      setError(err.response?.data?.error || 'Error al subir la fotografía. Intenta de nuevo.');
      return false;
    } finally {
      setPropertyImageUploading(false);
    }
  };

  // Cargar datos del usuario al montar el componente
  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      setLoading(true);
      
      // Obtener información del usuario actual
      const userData = await userService.getCurrentUser();
      console.log('📋 Datos del usuario obtenidos:', userData);
      
      const loadedNames = {
        firstName: isNameMissing(userData.firstName) ? '' : userData.firstName,
        firstLastName: isNameMissing(userData.firstLastName) ? '' : userData.firstLastName,
        secondLastName: isNameMissing(userData.secondLastName) ? '' : userData.secondLastName,
      };
      setSavedNames(loadedNames);
      setUserProfile({
        ...loadedNames,
        email: userData.email || '',
        username: userData.username || '',
        role: userData.role || null,
        company: userData.company || null,
        isActive: userData.isActive || false,
        createdAt: userData.createdAt || null,
        theme: userData.preferences?.theme || 'light',
        language: userData.preferences?.language || 'es',
        timezone: userData.preferences?.timezone || 'America/Mexico_City'
      });

      if (userData.company) {
        const c = userData.company;
        setCompanyData({
          businessName: c.businessName || '',
          legalName: c.legalName || '',
          rfc: c.rfc || '',
          requiresInvoice: !!c.requiresInvoice,
          fiscalStreet: c.fiscalStreet || '',
          fiscalExteriorNumber: c.fiscalExteriorNumber || '',
          fiscalInteriorNumber: c.fiscalInteriorNumber || '',
          fiscalNeighborhood: c.fiscalNeighborhood || '',
          fiscalMunicipality: c.fiscalMunicipality || '',
          fiscalState: c.fiscalState || '',
          fiscalPostalCode: c.fiscalPostalCode || '',
          fiscalReference: c.fiscalReference || '',
          fiscalBetweenStreets: c.fiscalBetweenStreets || '',
          fiscalCountry: c.fiscalCountry || 'México',
        });

        // Cargar el logo y la fotografía del inmueble ya guardados (si existen) para
        // mostrarlos de una vez, en vez de solo un texto de "ya hay uno registrado".
        if (c.hasLogo) {
          loadCompanyImagePreview(c.id, 'logo', setLogoPreview);
        }
        if (c.hasPropertyImage) {
          loadCompanyImagePreview(c.id, 'property-image', setPropertyImagePreview);
        }
      }


    } catch (error) {
      console.error('Error cargando datos del usuario:', error);
      setError('Error cargando la información del perfil');
      
      // Fallback: usar datos del sessionStorage si están disponibles
      const userInfo = JSON.parse(sessionStorage.getItem('userInfo') || '{}');
      if (userInfo.id) {
        setUserProfile(prev => ({
          ...prev,
          firstName: userInfo.firstName || '',
          lastName: userInfo.lastName || '',
          email: userInfo.email || '',
          username: userInfo.username || '',
          role: userInfo.role || null,
          company: userInfo.company || null,
          isActive: userInfo.isActive || false
        }));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Handlers optimizados con useCallback
  const handleProfileChange = useCallback((field, value) => {
    setUserProfile(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      setError('');
      
      const updateData = {
        firstName: userProfile.firstName,
        firstLastName: userProfile.firstLastName,
        secondLastName: userProfile.secondLastName
      };
      
      const saved = await userService.updateProfile(updateData);
      const savedData = saved || {};
      const newNames = {
        firstName: isNameMissing(savedData.firstName) ? '' : savedData.firstName,
        firstLastName: isNameMissing(savedData.firstLastName) ? '' : savedData.firstLastName,
        secondLastName: isNameMissing(savedData.secondLastName) ? '' : savedData.secondLastName,
      };
      setSavedNames(newNames);
      setUserProfile(prev => ({ ...prev, ...newNames }));
      
      setSuccess('Perfil actualizado correctamente');
      setEditMode(false);
      
      // Actualizar sessionStorage si existe
      const userInfo = JSON.parse(sessionStorage.getItem('userInfo') || '{}');
      if (userInfo.id) {
        const updatedUserInfo = { ...userInfo, ...newNames };
        sessionStorage.setItem('userInfo', JSON.stringify(updatedUserInfo));
      }
      
    } catch (error) {
      console.error('Error actualizando perfil:', error);
      setError(error.response?.data?.error || 'Error al actualizar el perfil. Inténtalo de nuevo.');
    } finally {
      setSaving(false);
      setTimeout(() => {
        setSuccess('');
        setError('');
      }, 3000);
    }
  };

  const handleChangePassword = async () => {
    try {
      setError('');
      
      // Validaciones
      if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
        setError('Todos los campos son obligatorios');
        return;
      }
      
      if (passwordData.newPassword !== passwordData.confirmPassword) {
        setError('Las contraseñas no coinciden');
        return;
      }
      
      if (passwordData.newPassword.length < 6) {
        setError('La nueva contraseña debe tener al menos 6 caracteres');
        return;
      }
      
      await userService.changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      
      setSuccess('Contraseña cambiada correctamente');
      setChangePasswordDialog(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      
    } catch (error) {
      console.error('Error cambiando contraseña:', error);
      setError('Error al cambiar la contraseña. Verifica tu contraseña actual.');
    } finally {
      setTimeout(() => {
        setSuccess('');
        setError('');
      }, 3000);
    }
  };

  const handleSavePreferences = async () => {
    try {
      setSaving(true);
      setError('');
      
      const preferences = {
        theme: userProfile.theme,
        language: userProfile.language,
        timezone: userProfile.timezone
      };
      
      await userService.updatePreferences(preferences);
      setSuccess('Preferencias guardadas correctamente');
      
    } catch (error) {
      console.error('Error guardando preferencias:', error);
      setError('Error al guardar las preferencias');
    } finally {
      setSaving(false);
      setTimeout(() => {
        setSuccess('');
        setError('');
      }, 3000);
    }
  };

  if (loading) {
    return (
      <Box sx={{ flexGrow: 1, bgcolor: '#f5f5f5', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Box sx={{ maxWidth: '1400px', mx: 'auto', px: 3, py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              badgeContent={
                <IconButton size="small" sx={{ bgcolor: 'white', boxShadow: 1 }}>
                  <PhotoCameraIcon fontSize="small" />
                </IconButton>
              }
            >
              <Avatar 
                sx={{ width: 80, height: 80, mr: 3, bgcolor: '#1976d2', fontSize: '2rem' }}
              >
                {(savedNames.firstName?.[0] || 'U').toUpperCase()}{(savedNames.firstLastName?.[0] || '').toUpperCase()}
              </Avatar>
            </Badge>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
                {[savedNames.firstName, savedNames.firstLastName, savedNames.secondLastName].filter(Boolean).join(' ') || userProfile.email}
              </Typography>
              {/* Eliminado campo rol visualización */}
              <Typography variant="body2" color="text.secondary">
                {userProfile.email}
              </Typography>
              {userProfile.createdAt && (
                <Typography variant="body2" color="text.secondary">
                  Miembro desde {new Date(userProfile.createdAt).getFullYear()}
                </Typography>
              )}
            </Box>
          </Box>
          <Chip 
            label={userProfile.isActive ? 'Activo' : 'Inactivo'} 
            color={userProfile.isActive ? 'success' : 'error'}
            sx={{ fontSize: '1rem', px: 2, py: 1 }}
          />
        </Box>

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}


        {/* Only show global errors for non-password actions */}
        {error && !changePasswordDialog && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs value={activeTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
            <Tab icon={<PersonIcon />} label="Información General" value={0} />
            <Tab icon={<SecurityIcon />} label="Seguridad" value={1} />
            <Tab icon={<SettingsIcon />} label="Preferencias" value={2} />
            {/* "Mi Actividad" mostraba datos inventados; solo el invitado tiene hoy un avance real que ver */}
            {isInvitado && <Tab icon={<AssessmentIcon />} label="Avance de mi servicio" value={3} />}
            <Tab icon={<NotificationsIcon />} label="Notificaciones" value={4} />
          </Tabs>
        </Paper>

        {/* Personal Information Tab */}
        <TabPanel value={activeTab} index={0}>
          {(() => {
            const filledCount = NAME_FIELDS.filter(f => !isNameMissing(savedNames[f.key])).length;
            const guestDirty = isInvitado && NAME_FIELDS.some(f =>
              isNameMissing(savedNames[f.key]) && (userProfile[f.key] || '').trim() !== '');
            const fieldEditable = (key) => isInvitado ? isNameMissing(savedNames[key]) : editMode;
            return (
              <Paper elevation={0} sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid #e0e7ff', boxShadow: '0 4px 20px rgba(25,118,210,0.08)' }}>
                <Box sx={{ px: 4, py: 2.5, background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <PersonIcon sx={{ color: 'white', fontSize: 22 }} />
                  <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, fontSize: '1rem', flexGrow: 1 }}>
                    Información General
                  </Typography>
                  {isInvitado ? (
                    <Chip
                      size="small"
                      icon={filledCount === NAME_FIELDS.length ? <CheckCircleIcon sx={{ color: 'white !important' }} /> : undefined}
                      label={`${filledCount} de ${NAME_FIELDS.length} datos completos`}
                      sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: 'white', fontWeight: 600 }}
                    />
                  ) : (
                    <Button
                      variant={editMode ? 'outlined' : 'contained'}
                      startIcon={<EditIcon />}
                      onClick={() => {
                        if (editMode) setUserProfile(prev => ({ ...prev, ...savedNames }));
                        setEditMode(!editMode);
                      }}
                      sx={{ bgcolor: editMode ? 'transparent' : 'white', color: editMode ? 'white' : '#1565c0', borderColor: 'white',
                        '&:hover': { bgcolor: editMode ? 'rgba(255,255,255,0.12)' : '#e3f2fd' } }}
                    >
                      {editMode ? 'Cancelar' : 'Editar'}
                    </Button>
                  )}
                </Box>

                <Box sx={{ p: 4 }}>
                  <Grid container spacing={3}>
                    {NAME_FIELDS.map(({ key, label }) => {
                      const editable = fieldEditable(key);
                      const pending = isInvitado && editable;
                      return (
                        <Grid key={key} size={{ xs: 12, md: 6 }}>
                          <TextField
                            fullWidth
                            label={label}
                            value={userProfile[key] || ''}
                            disabled={!editable}
                            placeholder={pending ? `Escribe tu ${label.toLowerCase()}` : undefined}
                            InputLabelProps={pending ? { shrink: true } : undefined}
                            onChange={(e) => handleProfileChange(key, sanitizeNameInput(e.target.value))}
                            onBlur={(e) => e.target.value && handleProfileChange(key, capitalizePersonName(e.target.value))}
                            inputProps={{ maxLength: FIELD_LIMITS.personName }}
                            helperText={isInvitado
                              ? (pending ? 'Pendiente — agrégalo' : 'Para corregirlo, contacta a CONEM')
                              : undefined}
                            FormHelperTextProps={pending ? { sx: { color: '#ed6c02', fontWeight: 600 } } : undefined}
                            InputProps={isInvitado && !editable
                              ? { endAdornment: <LockIcon sx={{ fontSize: 18, color: 'text.disabled' }} /> }
                              : undefined}
                            sx={pending ? {
                              '& .MuiOutlinedInput-root': { bgcolor: '#fff8e1' },
                              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ffb74d', borderWidth: 2 },
                            } : undefined}
                          />
                        </Grid>
                      );
                    })}
                  </Grid>

                  {(isInvitado ? guestDirty : editMode) && (
                    <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                      <Button
                        variant="contained"
                        startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                        onClick={handleSaveProfile}
                        disabled={saving}
                      >
                        {saving ? 'Guardando...' : 'Guardar Cambios'}
                      </Button>
                    </Box>
                  )}
                </Box>
              </Paper>
            );
          })()}

          {/* Sección empresa — solo invitados */}
          {isInvitado && userProfile.company && (
            <Paper elevation={0} sx={{ mt: 3, borderRadius: 3, overflow: 'hidden', border: '1px solid #e0e7ff', boxShadow: '0 4px 20px rgba(25,118,210,0.08)' }}>
              {/* Header */}
              <Box sx={{ px: 4, py: 2.5, background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <BusinessIcon sx={{ color: 'white', fontSize: 22 }} />
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, fontSize: '1rem' }}>
                  Datos de la Empresa
                </Typography>
              </Box>

              <Box sx={{ p: 4 }}>
                {/* Bloque: Identificación */}
                <Typography variant="overline" sx={{ color: '#1976d2', fontWeight: 700, letterSpacing: 1.2, display: 'block', mb: 2 }}>
                  Identificación
                </Typography>
                <Grid container spacing={2.5} sx={{ mb: 4 }}>
                  <Grid size={{ xs: 12, md: 5 }}>
                    <TextField fullWidth label="Nombre Comercial" value={companyData.businessName}
                      disabled
                      helperText="Una vez capturado, el nombre comercial no puede modificarse."
                      inputProps={{ maxLength: FIELD_LIMITS.businessName }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                  </Grid>
                  <Grid size={{ xs: 12, md: 5 }}>
                    <TextField fullWidth label="Razón Social" value={companyData.legalName}
                      onChange={(e) => setCompanyData(prev => ({ ...prev, legalName: e.target.value }))}
                      inputProps={{ maxLength: FIELD_LIMITS.legalName }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                    <CaseTypoHint value={companyData.legalName}
                      onFix={(fixed) => setCompanyData(prev => ({ ...prev, legalName: fixed }))} />
                  </Grid>
                  <Grid size={{ xs: 12, md: 2 }}>
                    <TextField fullWidth label="RFC" value={companyData.rfc}
                      onChange={(e) => setCompanyData(prev => ({ ...prev, rfc: e.target.value.toUpperCase() }))}
                      inputProps={{ maxLength: 13, style: { fontFamily: 'monospace', letterSpacing: 1 } }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                  </Grid>
                </Grid>

                {/* Bloque: Domicilio Fiscal */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Typography variant="overline" sx={{ color: '#1976d2', fontWeight: 700, letterSpacing: 1.2 }}>
                    Dirección Fiscal (para facturación)
                  </Typography>
                  <Box sx={{ flex: 1, height: '1px', bgcolor: '#e0e7ff' }} />
                </Box>
                <Grid container spacing={2.5} sx={{ mb: 4 }}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField fullWidth label="Calle" value={companyData.fiscalStreet}
                      onChange={(e) => setCompanyData(prev => ({ ...prev, fiscalStreet: e.target.value }))}
                      inputProps={{ maxLength: 100 }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                  </Grid>
                  <Grid size={{ xs: 6, md: 3 }}>
                    <TextField fullWidth label="No. Exterior" value={companyData.fiscalExteriorNumber}
                      onChange={(e) => setCompanyData(prev => ({ ...prev, fiscalExteriorNumber: e.target.value.replace(/[^0-9]/g, '') }))}
                      inputProps={{ maxLength: 4 }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                  </Grid>
                  <Grid size={{ xs: 6, md: 3 }}>
                    <TextField fullWidth label="No. Interior" value={companyData.fiscalInteriorNumber}
                      onChange={(e) => setCompanyData(prev => ({ ...prev, fiscalInteriorNumber: e.target.value }))}
                      inputProps={{ maxLength: 4 }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField fullWidth label="Colonia" value={companyData.fiscalNeighborhood}
                      onChange={(e) => setCompanyData(prev => ({ ...prev, fiscalNeighborhood: e.target.value }))}
                      inputProps={{ maxLength: 100 }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField fullWidth label="Municipio / Alcaldía" value={companyData.fiscalMunicipality}
                      onChange={(e) => setCompanyData(prev => ({ ...prev, fiscalMunicipality: e.target.value }))}
                      inputProps={{ maxLength: 50 }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField fullWidth label="Código Postal" value={companyData.fiscalPostalCode}
                      onChange={(e) => setCompanyData(prev => ({ ...prev, fiscalPostalCode: e.target.value.replace(/[^0-9]/g, '') }))}
                      inputProps={{ maxLength: 5 }}
                      helperText="5 dígitos"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <FormControl fullWidth>
                      <InputLabel>Estado</InputLabel>
                      <Select value={companyData.fiscalState} label="Estado"
                        onChange={(e) => setCompanyData(prev => ({ ...prev, fiscalState: e.target.value }))}
                        sx={{ borderRadius: 2 }}>
                        {['Aguascalientes','Baja California','Baja California Sur','Campeche','Chiapas','Chihuahua',
                          'Ciudad de México','Coahuila','Colima','Durango','Estado de México','Guanajuato','Guerrero',
                          'Hidalgo','Jalisco','Michoacán','Morelos','Nayarit','Nuevo León','Oaxaca','Puebla','Querétaro',
                          'Quintana Roo','San Luis Potosí','Sinaloa','Sonora','Tabasco','Tamaulipas','Tlaxcala',
                          'Veracruz','Yucatán','Zacatecas'].map(s => (
                          <MenuItem key={s} value={s}>{s}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField fullWidth label="País" value={companyData.fiscalCountry}
                      onChange={(e) => setCompanyData(prev => ({ ...prev, fiscalCountry: e.target.value }))}
                      inputProps={{ maxLength: 50 }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField fullWidth label="Entre Calles" value={companyData.fiscalBetweenStreets}
                      onChange={(e) => setCompanyData(prev => ({ ...prev, fiscalBetweenStreets: e.target.value }))}
                      inputProps={{ maxLength: 100 }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField fullWidth label="Referencia" value={companyData.fiscalReference}
                      onChange={(e) => setCompanyData(prev => ({ ...prev, fiscalReference: e.target.value }))}
                      inputProps={{ maxLength: 100 }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                  </Grid>
                </Grid>

                {/* Bloque: Facturación */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Typography variant="overline" sx={{ color: '#1976d2', fontWeight: 700, letterSpacing: 1.2 }}>
                    Facturación
                  </Typography>
                  <Box sx={{ flex: 1, height: '1px', bgcolor: '#e0e7ff' }} />
                </Box>
                <Box sx={{ p: 2.5, borderRadius: 2, bgcolor: companyData.requiresInvoice ? '#e3f2fd' : '#f8faff', border: '1px solid', borderColor: companyData.requiresInvoice ? '#90caf9' : '#e0e7ff', mb: companyData.requiresInvoice ? 2 : 0 }}>
                  <FormControlLabel
                    control={<Switch checked={!!companyData.requiresInvoice}
                      onChange={(e) => setCompanyData(prev => ({ ...prev, requiresInvoice: e.target.checked }))}
                      color="primary" />}
                    label={
                      <Box>
                        <Typography variant="body1" fontWeight={600}>Requiere Factura</Typography>
                        <Typography variant="caption" color="text.secondary">Active esta opción si necesita factura por los servicios contratados</Typography>
                      </Box>
                    }
                  />
                </Box>
                {companyData.requiresInvoice && (
                  <Box sx={{ p: 2.5, borderRadius: 2, bgcolor: '#fff8e1', border: '1px solid #ffe082' }}>
                    <Typography variant="body2" sx={{ mb: 2, color: '#5d4037' }}>
                      Suba su <strong>Constancia de Situación Fiscal</strong> actualizada (no mayor a 3 meses) o envíela a{' '}
                      <strong>info@conem.com.mx</strong>.
                    </Typography>
                    {userProfile.company.hasConstancia && !constanciaFile && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <CheckCircleIcon sx={{ color: '#43a047', fontSize: 18 }} />
                        <Typography variant="body2" color="text.secondary">Constancia registrada. Puede actualizarla subiendo un nuevo archivo.</Typography>
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                      <input ref={constanciaInputRef} type="file" accept="application/pdf" style={{ display: 'none' }} onChange={handleConstanciaSelect} />
                      <Button variant="outlined" size="small" startIcon={<CloudUploadIcon />}
                        onClick={() => constanciaInputRef.current?.click()}
                        sx={{ textTransform: 'none', borderRadius: 2, borderColor: '#1976d2', color: '#1976d2' }}>
                        {constanciaFile ? constanciaFile.name : 'Seleccionar PDF'}
                      </Button>
                      {constanciaFile && (
                        <Button variant="contained" size="small" onClick={handleConstanciaUpload} disabled={constanciaUploading}
                          startIcon={constanciaUploading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                          sx={{ textTransform: 'none', borderRadius: 2 }}>
                          {constanciaUploading ? 'Subiendo...' : 'Subir'}
                        </Button>
                      )}
                      {constanciaSuccess && <Chip label="¡Subida correctamente!" color="success" size="small" icon={<CheckCircleIcon />} />}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>PDF · máx. 10 MB</Typography>
                  </Box>
                )}

                {/* Botón guardar */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
                  <Button variant="contained" onClick={handleSaveCompanyClick}
                    disabled={savingCompany || constanciaUploading || logoUploading || propertyImageUploading}
                    startIcon={savingCompany ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                    sx={{ textTransform: 'none', borderRadius: 2, px: 4, fontWeight: 600,
                      background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                      boxShadow: '0 4px 12px rgba(25,118,210,0.3)',
                      '&:hover': { background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)' } }}>
                    {savingCompany ? 'Guardando...' : 'Guardar Datos de la Empresa'}
                  </Button>
                </Box>
              </Box>
            </Paper>
          )}

          <Dialog open={pendingUploadConfirm} onClose={() => setPendingUploadConfirm(false)} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 700 }}>Tienes archivos seleccionados sin subir</DialogTitle>
            <DialogContent>
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                Elegiste estos archivos pero no les diste clic en "Subir". ¿Quieres subirlos junto con los datos de la empresa?
              </Typography>
              {pendingUploads.map(u => (
                <Typography key={u.label} variant="body2" sx={{ ml: 1 }}>
                  • <strong>{u.label}:</strong> {u.file.name}
                </Typography>
              ))}
            </DialogContent>
            <DialogActions sx={{ p: 2, gap: 1 }}>
              <Button onClick={() => setPendingUploadConfirm(false)} sx={{ textTransform: 'none' }}>
                Volver
              </Button>
              <Button variant="outlined" sx={{ textTransform: 'none' }}
                onClick={() => { setPendingUploadConfirm(false); handleSaveCompany(); }}>
                Guardar sin subirlos
              </Button>
              <Button variant="contained" sx={{ textTransform: 'none' }}
                onClick={() => { setPendingUploadConfirm(false); handleSaveCompany({ uploadPending: true }); }}>
                Subir y guardar
              </Button>
            </DialogActions>
          </Dialog>

          {/* Sección logo empresa — solo invitados */}
          {isInvitado && userProfile.company && (
            <Paper sx={{ p: 4, mt: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <BusinessIcon sx={{ color: '#1976d2' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Logo de su Empresa
                </Typography>
              </Box>

              <Alert severity="info" sx={{ mb: 3 }}>
                Para efectos del desarrollo de su <strong>Programa Interno de Protección Civil</strong>, requerimos que nos confirme o comparta el logo de su empresa. Favor de subir su logo en formato <strong>.jpg</strong> o <strong>.png</strong>.
              </Alert>

              {userProfile.company.hasLogo && (
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleIcon sx={{ color: '#43a047', fontSize: 20 }} />
                  <Typography variant="body2" color="text.secondary">
                    Logo registrado. Puede actualizarlo subiendo un nuevo archivo.
                  </Typography>
                </Box>
              )}

              {/* Preview: logo ya guardado, o el que se acaba de seleccionar */}
              {logoPreview && (
                <Box sx={{ mb: 2, border: '1px solid #e0e0e0', borderRadius: 2, p: 1.5, display: 'inline-block' }}>
                  <img src={logoPreview} alt="Logo de la empresa" style={{ width: 200, height: 'auto', maxHeight: 120, maxWidth: 300, objectFit: 'contain', display: 'block' }} />
                </Box>
              )}

              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                  ref={logoInputRef}
                  type="file"
                  accept={JPG_PNG_ACCEPT}
                  style={{ display: 'none' }}
                  onChange={handleLogoSelect}
                />
                <Button
                  variant="outlined"
                  startIcon={<CloudUploadIcon />}
                  onClick={() => logoInputRef.current?.click()}
                  sx={{ textTransform: 'none' }}
                >
                  {logoFile ? logoFile.name : 'Seleccionar archivo .jpg o .png'}
                </Button>
                {logoFile && (
                  <Button
                    variant="contained"
                    onClick={handleLogoUpload}
                    disabled={logoUploading}
                    startIcon={logoUploading ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                    sx={{ textTransform: 'none' }}
                  >
                    {logoUploading ? 'Subiendo...' : 'Subir Logo'}
                  </Button>
                )}
              </Box>

              {logoSuccess && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  Logo subido correctamente. ¡Gracias!
                </Alert>
              )}

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                Formato: JPG o PNG · Tamaño máximo: 5 MB
              </Typography>
            </Paper>
          )}

          {/* Sección foto del inmueble (matriz) — solo invitados */}
          {isInvitado && userProfile.company && (
            <Paper sx={{ p: 4, mt: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <BusinessIcon sx={{ color: '#1976d2' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Fotografía del Inmueble
                </Typography>
              </Box>

              <Alert severity="info" sx={{ mb: 3 }}>
                Comparta una fotografía de la fachada o el inmueble de su empresa (matriz) para incluirla en su Programa Interno de Protección Civil.
              </Alert>

              {userProfile.company.hasPropertyImage && (
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleIcon sx={{ color: '#43a047', fontSize: 20 }} />
                  <Typography variant="body2" color="text.secondary">
                    Fotografía registrada. Puede actualizarla subiendo un nuevo archivo.
                  </Typography>
                </Box>
              )}

              {/* Preview: fotografía ya guardada, o la que se acaba de seleccionar */}
              {propertyImagePreview && (
                <Box sx={{ mb: 2, border: '1px solid #e0e0e0', borderRadius: 2, p: 1.5, display: 'inline-block' }}>
                  <img src={propertyImagePreview} alt="Fotografía del inmueble" style={{ maxHeight: 160, maxWidth: 320, objectFit: 'contain' }} />
                </Box>
              )}

              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                  ref={propertyImageInputRef}
                  type="file"
                  accept={JPG_PNG_ACCEPT}
                  style={{ display: 'none' }}
                  onChange={handlePropertyImageSelect}
                />
                <Button
                  variant="outlined"
                  startIcon={<CloudUploadIcon />}
                  onClick={() => propertyImageInputRef.current?.click()}
                  sx={{ textTransform: 'none' }}
                >
                  {propertyImageFile ? propertyImageFile.name : 'Seleccionar archivo .jpg o .png'}
                </Button>
                {propertyImageFile && (
                  <Button
                    variant="contained"
                    onClick={handlePropertyImageUpload}
                    disabled={propertyImageUploading}
                    startIcon={propertyImageUploading ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                    sx={{ textTransform: 'none' }}
                  >
                    {propertyImageUploading ? 'Subiendo...' : 'Subir Fotografía'}
                  </Button>
                )}
              </Box>

              {propertyImageSuccess && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  Fotografía subida correctamente. ¡Gracias!
                </Alert>
              )}

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                Formato: JPG o PNG · Tamaño máximo: 8 MB
              </Typography>
            </Paper>
          )}
        </TabPanel>

        {/* Security Tab */}
        <TabPanel value={activeTab} index={1}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Contraseña
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Última actualización: 15 de enero, 2024
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<LockIcon />}
                  onClick={() => setChangePasswordDialog(true)}
                >
                  Cambiar Contraseña
                </Button>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Autenticación de Dos Factores
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Agrega una capa extra de seguridad a tu cuenta
                </Typography>
                <FormControlLabel
                  control={<Switch defaultChecked />}
                  label="Habilitar 2FA"
                />
              </Paper>
            </Grid>
          </Grid>
          {!loading && !isInvitado && <ApiTokensSection />}
        </TabPanel>

        {/* Preferences Tab */}
        <TabPanel value={activeTab} index={2}>
          <Paper sx={{ p: 4 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Preferencias del Sistema
            </Typography>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Tema</InputLabel>
                  <Select
                    value={userProfile.theme}
                    label="Tema"
                    onChange={(e) => setUserProfile({...userProfile, theme: e.target.value})}
                  >
                    <MenuItem value="light">Claro</MenuItem>
                    <MenuItem value="dark">Oscuro</MenuItem>
                    <MenuItem value="auto">Automático</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Idioma</InputLabel>
                  <Select
                    value={userProfile.language}
                    label="Idioma"
                    onChange={(e) => setUserProfile({...userProfile, language: e.target.value})}
                  >
                    <MenuItem value="es">Español</MenuItem>
                    <MenuItem value="en">English</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <Box sx={{ mt: 3 }}>
              <Button 
                variant="contained" 
                startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                onClick={handleSavePreferences}
                disabled={saving}
              >
                {saving ? 'Guardando...' : 'Guardar Preferencias'}
              </Button>
            </Box>
          </Paper>
        </TabPanel>

        {/* Avance de mi servicio (solo invitado) */}
        {isInvitado && (
          <TabPanel value={activeTab} index={3}>
            <AvanceMiServicio />
          </TabPanel>
        )}

        {/* Notifications Tab */}
        <TabPanel value={activeTab} index={4}>
          <Paper sx={{ p: 4 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Configuración de Notificaciones
            </Typography>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                  Canales de Notificación
                </Typography>
                <FormControlLabel
                  control={<Switch defaultChecked />}
                  label="Notificaciones por Email"
                />
                <FormControlLabel
                  control={<Switch defaultChecked />}
                  label="Notificaciones Push"
                />
                <FormControlLabel
                  control={<Switch defaultChecked />}
                  label="Notificaciones en la App"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Frecuencia</InputLabel>
                  <Select defaultValue="immediate" label="Frecuencia">
                    <MenuItem value="immediate">Inmediata</MenuItem>
                    <MenuItem value="hourly">Cada Hora</MenuItem>
                    <MenuItem value="daily">Diaria</MenuItem>
                    <MenuItem value="weekly">Semanal</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <Box sx={{ mt: 3 }}>
              <Button variant="contained" startIcon={<SaveIcon />}>
                Guardar Configuración
              </Button>
            </Box>
          </Paper>
        </TabPanel>

        {/* Change Password Dialog */}
        <Dialog open={changePasswordDialog} onClose={() => setChangePasswordDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Cambiar Contraseña</DialogTitle>
          <DialogContent>
            {/* Show error only inside modal when changing password */}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <TextField
              fullWidth
              type="password"
              label="Contraseña Actual"
              value={passwordData.currentPassword}
              onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
              margin="normal"
            />
            <TextField
              fullWidth
              type="password"
              label="Nueva Contraseña"
              value={passwordData.newPassword}
              onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
              margin="normal"
            />
            <TextField
              fullWidth
              type="password"
              label="Confirmar Nueva Contraseña"
              value={passwordData.confirmPassword}
              onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
              margin="normal"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setChangePasswordDialog(false)}>Cancelar</Button>
            <Button onClick={handleChangePassword} variant="contained">Cambiar Contraseña</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default Profile;
